import isEqual from "lodash-es/isEqual";
import { Fragment, ReactNode, useCallback, useEffect } from "react";
import {
  ArrayPath,
  Control,
  FieldArray,
  FieldValue,
  FieldValues,
  useFieldArray,
  useFormContext,
  Validate,
} from "react-hook-form";

import { Trash } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import LinkAdd from "ds/components/Link/LinkAdd";

import styles from "./styles.module.css";

type FormArrayFieldProps<T extends FieldValues, K extends ArrayPath<T>> = {
  name: K;
  addButtonLabel: string;
  emptyValue: FieldValue<T>;
  disabled?: boolean;
  validate?: Validate<FieldArray<T, K>[], T> | Record<string, Validate<FieldArray<T, K>[], T>>;
  children: (props: {
    value: T[K][number];
    control: Control<T>;
    field: Record<"id", string>;
    index: number;
    suffix: ReactNode;
    addEmptyItem?: () => void;
  }) => ReactNode;
  onAdd?: () => void;
  onRemove?: (index: number) => void;
  isAddDisabled?: boolean;
};

const FormArrayField = <T extends FieldValues, K extends ArrayPath<T>>({
  name,
  children,
  addButtonLabel,
  emptyValue,
  disabled,
  onAdd,
  onRemove,
  validate,
  isAddDisabled = false,
}: FormArrayFieldProps<T, K>) => {
  const { control, watch } = useFormContext<T>();
  const values = watch();
  const { fields, append, remove, replace } = useFieldArray<T, K>({
    name,
    rules: {
      validate,
    },
  });

  const handleRemoveRow = (index: number) => () => {
    if (fields.length === 1) {
      replace([emptyValue]);
    } else {
      remove(index);
    }

    onRemove?.(index);
  };

  const addEmptyItem = useCallback(() => {
    append(emptyValue);
    onAdd?.();
  }, [append, emptyValue, onAdd]);

  const isRemoveDisabled = (value: FieldValue<T>) => hasOneField && isEqual(value, emptyValue);

  useEffect(() => {
    if (!fields.length) {
      replace([emptyValue]);
    }
  }, [emptyValue, fields.length, replace]);

  const hasOneField = fields.length === 1;

  return (
    <Box direction="column" gap="large" align="start" fullWidth>
      {fields.map((field, index) => {
        const value = values[name][index];

        return (
          <Fragment key={field.id}>
            {children({
              value,
              control,
              field,
              index,
              addEmptyItem: index === fields.length - 1 && value.value ? addEmptyItem : undefined,
              suffix: (
                <ButtonIcon
                  className={styles.removeButton}
                  icon={Trash}
                  disabled={disabled || isRemoveDisabled(value)}
                  onPress={handleRemoveRow(index)}
                  variant="ghostDanger"
                >
                  Delete
                </ButtonIcon>
              ),
            })}
          </Fragment>
        );
      })}
      <LinkAdd text={addButtonLabel} onClick={addEmptyItem} disabled={disabled || isAddDisabled} />
    </Box>
  );
};

FormArrayField.displayName = "DS.Form.ArrayField";

export default FormArrayField;
